import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { allComics } from '@/mocks/comics';
import type { ComicData, ChapterData } from '@/mocks/comics';

const comicChapters: Record<string, ChapterData[]> = {
  'queen-of-tears': Array.from({ length: 48 }, (_, i) => ({
    id: `ch-${48 - i}`,
    title: `第${48 - i}话${i === 0 ? ' - 最新' : ''}`,
    updateTime: `2026-${String(6 - Math.floor(i / 4)).padStart(2, '0')}-${String(30 - (i % 15)).padStart(2, '0')}`,
  })),
  'blade-of-destiny': Array.from({ length: 62 }, (_, i) => ({
    id: `ch-${62 - i}`,
    title: `第${62 - i}话${i === 0 ? ' - 最新' : ''}`,
    updateTime: `2026-${String(6 - Math.floor(i / 4)).padStart(2, '0')}-${String(28 - (i % 14)).padStart(2, '0')}`,
  })),
  'magic-academy': Array.from({ length: 55 }, (_, i) => ({
    id: `ch-${55 - i}`,
    title: `第${55 - i}话${i === 0 ? ' - 最新' : ''}`,
    updateTime: `2026-${String(6 - Math.floor(i / 4)).padStart(2, '0')}-${String(27 - (i % 13)).padStart(2, '0')}`,
  })),
  'night-detective': Array.from({ length: 41 }, (_, i) => ({
    id: `ch-${41 - i}`,
    title: `第${41 - i}话${i === 0 ? ' - 最新' : ''}`,
    updateTime: `2026-${String(6 - Math.floor(i / 4)).padStart(2, '0')}-${String(25 - (i % 12)).padStart(2, '0')}`,
  })),
  'puppy-love': Array.from({ length: 32 }, (_, i) => ({
    id: `ch-${32 - i}`,
    title: `第${32 - i}话${i === 0 ? ' - 最新' : ''}`,
    updateTime: `2026-${String(6 - Math.floor(i / 4)).padStart(2, '0')}-${String(22 - (i % 11)).padStart(2, '0')}`,
  })),
  'dragon-warrior': Array.from({ length: 39 }, (_, i) => ({
    id: `ch-${39 - i}`,
    title: `第${39 - i}话${i === 0 ? ' - 最新' : ''}`,
    updateTime: `2026-${String(6 - Math.floor(i / 4)).padStart(2, '0')}-${String(20 - (i % 10)).padStart(2, '0')}`,
  })),
  'starry-sky': Array.from({ length: 60 }, (_, i) => ({
    id: `ch-${60 - i}`,
    title: `第${60 - i}话${i === 0 ? '（完结）' : ''}`,
    updateTime: `2026-${String(5 - Math.floor(i / 5)).padStart(2, '0')}-${String(18 - (i % 9)).padStart(2, '0')}`,
  })),
  'shadow-king': Array.from({ length: 71 }, (_, i) => ({
    id: `ch-${71 - i}`,
    title: `第${71 - i}话${i === 0 ? ' - 最新' : ''}`,
    updateTime: `2026-${String(6 - Math.floor(i / 4)).padStart(2, '0')}-${String(33 - (i % 16)).padStart(2, '0')}`,
  })),
};

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = allComics.find((c) => c.slug === slug);
  const chapters = slug ? (comicChapters[slug] || []) : [];
  const relatedComics = allComics.filter((c) => c.slug !== slug).slice(0, 4);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900">漫画不存在</h1>
            <Link to="/" className="text-primary-500 mt-4 inline-block cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = `${comic.title} - ${comic.author}作品 - 韩漫之家免费在线观看`;
  const pageDesc = `韩漫之家提供${comic.title}免费在线阅读，${comic.description.substring(0, 80)}... 作者：${comic.author}，状态：${comic.status}，评分${comic.rating}分。`;

  return (
    <>
      <Navbar />
      <main>
        <div className="hidden">
          <meta data-page-title={pageTitle} />
        </div>

        <section className="pt-20 pb-10 md:pt-24 md:pb-14 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center gap-2 text-foreground-400 text-xs mb-6" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line"></i>
                </span>
                <Link to={`/category/${comic.category === '恋爱' ? 'love' : comic.category === '动作' ? 'action' : comic.category === '奇幻' ? 'fantasy' : comic.category === '悬疑' ? 'suspense' : comic.category === '搞笑' ? 'comedy' : 'drama'}`} className="hover:text-primary-500 transition-colors cursor-pointer">{comic.category}</Link>
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line"></i>
                </span>
                <span className="text-foreground-700 font-medium">{comic.title}</span>
              </nav>

              <div className="flex flex-col lg:flex-row gap-8">
                <div className="w-full lg:w-[320px] flex-shrink-0">
                  <div className="relative rounded-lg overflow-hidden aspect-[5/7]">
                    <img src={comic.coverImage} alt={comic.title} className="w-full h-full object-cover" />
                    <span className="absolute top-3 right-3 text-xs font-medium px-2 py-1 rounded-full bg-primary-500/90 text-white">
                      {comic.status}
                    </span>
                  </div>
                </div>

                <div className="flex-1 flex flex-col gap-5">
                  <div>
                    <span className="text-xs text-primary-500 font-medium bg-primary-50 px-3 py-1 rounded-full whitespace-nowrap">
                      {comic.category}
                    </span>
                    <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-3">
                      {comic.title}
                      <span className="text-foreground-300 font-light ml-3 text-xl">{comic.author}</span>
                    </h1>
                  </div>

                  <p className="text-sm text-foreground-600 leading-relaxed bg-background-100 rounded-lg p-5">
                    {comic.description}
                  </p>

                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <div className="flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500"></i>
                      </span>
                      <span className="text-accent-500 font-bold text-lg">{comic.rating}</span>
                      <span className="text-foreground-400 text-xs">分</span>
                    </div>
                    <span className="text-foreground-300">|</span>
                    <span className="text-foreground-500">{comic.status}</span>
                    <span className="text-foreground-300">|</span>
                    <span className="text-foreground-500">{(comic.views / 10000).toFixed(0)}万次阅读</span>
                    <span className="text-foreground-300">|</span>
                    <span className="text-foreground-500">共 {chapters.length} 话</span>
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {comic.tags.map((tag) => (
                      <span key={tag} className="text-xs text-secondary-700 bg-secondary-100/80 px-3 py-1 rounded-full whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <Link
                      to={`/comic/${comic.slug}`}
                      className="bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium px-6 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors inline-flex items-center gap-2"
                    >
                      开始阅读
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-play-line"></i>
                      </span>
                    </Link>
                    <button className="border border-background-300 text-foreground-600 text-sm px-4 py-2.5 rounded-md whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors inline-flex items-center gap-1.5">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-bookmark-line"></i>
                      </span>
                      收藏
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-lg font-bold text-foreground-900 mb-6">章节列表</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {chapters.slice(0, 18).map((ch) => (
                  <Link
                    key={ch.id}
                    to={`/comic/${comic.slug}`}
                    className="flex items-center gap-3 p-4 rounded-lg bg-background-50 hover:bg-background-200/70 transition-colors cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center bg-secondary-100 rounded-full flex-shrink-0">
                      <i className="ri-file-text-line text-secondary-600 text-sm"></i>
                    </span>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-foreground-800 truncate block">{ch.title}</span>
                      <span className="text-xs text-foreground-400">{ch.updateTime}</span>
                    </div>
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-arrow-right-s-line text-foreground-400"></i>
                    </span>
                  </Link>
                ))}
              </div>
              {chapters.length > 18 && (
                <div className="text-center mt-6">
                  <button className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">
                    查看全部 {chapters.length} 话
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="py-10 md:py-14 bg-background-50">
            <div className="px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-lg font-bold text-foreground-900 mb-6">相关推荐</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop>
                  {relatedComics.map((rc) => (
                    <Link
                      key={rc.id}
                      to={`/comic/${rc.slug}`}
                      className="group relative rounded-lg overflow-hidden cursor-pointer flex flex-col bg-background-100 hover:bg-background-200/70 transition-colors"
                    >
                      <div className="relative aspect-[5/7] overflow-hidden">
                        <img src={rc.coverImage} alt={rc.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                          {rc.title}
                        </h3>
                        <p className="text-xs text-foreground-400 mt-1">{rc.author}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: pageTitle,
            description: pageDesc,
            author: {
              '@type': 'Person',
              name: comic.author,
            },
            datePublished: '2026-01-15',
            dateModified: comic.updateTime,
            url: `http://guanlist.com/comic/${comic.slug}`,
            image: comic.coverImage,
            publisher: {
              '@type': 'Organization',
              name: '韩漫之家',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `http://guanlist.com/comic/${comic.slug}`,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'http://guanlist.com/' },
              { '@type': 'ListItem', position: 2, name: comic.category, item: `http://guanlist.com/category/${comic.category === '恋爱' ? 'love' : comic.category === '动作' ? 'action' : comic.category === '奇幻' ? 'fantasy' : comic.category === '悬疑' ? 'suspense' : comic.category === '搞笑' ? 'comedy' : 'drama'}` },
              { '@type': 'ListItem', position: 3, name: comic.title },
            ],
          }),
        }}
      />
    </>
  );
}